<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, reactive, ref, onMounted, watch, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useImoveisStore } from 'dashboard/stores/imoveis';
import { useMapGetter } from 'dashboard/composables/store';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useAlert } from 'dashboard/composables';
import ImovelAPI from 'dashboard/api/imoveis';
import {
  buildTaxFeeGovernanceRows,
  TAX_FEE_CONFIDENCE_LABELS,
  TAX_FEE_SOURCE_LABELS,
} from 'dashboard/helper/lais/imovelPresenter';
import {
  cleanDeepObject,
  cleanObject,
  IMOVEL_FORM_OWNER,
  IMOVEL_FORM_OWNER_CONTRACT,
  normalizeImovelPayload,
  normalizeOptionalNumber,
  normalizeOptionalText,
} from 'dashboard/helper/lais/imovelPayload';

import LaisAddressField from 'dashboard/components-next/LaisAddressField.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisFieldFeedback from 'dashboard/components-next/lais/LaisFieldFeedback.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisTabs from 'dashboard/components-next/LaisTabs.vue';
import LaisPermissionState from 'dashboard/components-next/LaisPermissionState.vue';
import LaisCardSkeleton from 'dashboard/components-next/LaisCardSkeleton.vue';
import LaisErrorState from 'dashboard/components-next/LaisErrorState.vue';
import LaisConfirmModal from 'dashboard/components-next/LaisConfirmModal.vue';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { LAIS_IMOVEIS_PERMISSIONS } from 'dashboard/routes/dashboard/laisRouteHelpers';

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');
const store = useImoveisStore();
const { checkPermissions } = usePolicy();

const isEditing = !!route.params.id;
const isSaving = ref(false);
const isLoading = ref(false);
const loadError = ref(false);
const completion = ref(null);
const canManageImoveis = computed(() =>
  checkPermissions(LAIS_IMOVEIS_PERMISSIONS)
);

// Separação física / comercial / mídia: cada aba deixa explícito se o operador
// está editando o ativo, a oferta ou as fotos (F02 / FE-AUD-13).
const activeFormTab = ref('identidade');
const formTabs = computed(() => [
  {
    id: 'identidade',
    label: t('PROPERTIES.FORM.TABS.IDENTITY'),
    icon: 'i-lucide-map-pin',
  },
  {
    id: 'anuncio',
    label: t('PROPERTIES.FORM.TABS.LISTING'),
    icon: 'i-lucide-badge-dollar-sign',
  },
  {
    id: 'midia',
    label: t('PROPERTIES.FORM.TABS.MEDIA'),
    icon: 'i-lucide-images',
  },
]);
const flowOwnerContract = IMOVEL_FORM_OWNER_CONTRACT;
const canonicalOwnerLabel = flowOwnerContract.editorLabel;
const editorSectionLinks = [
  {
    id: 'estimativa_qpreco',
    label: 'Estimativa',
    target: 'imovel-price-estimate',
    icon: 'i-lucide-calculator',
  },
  {
    id: 'tax_fee_governance',
    label: 'Taxas',
    target: 'imovel-tax-fee-governance',
    icon: 'i-lucide-receipt-text',
  },
  {
    id: 'entry_access_keys',
    label: 'Acessos e chaves',
    target: 'imovel-key-access',
    icon: 'i-lucide-key-round',
  },
];
const MEDIA_PUBLICABILITY_FORM_REQUIREMENTS = Object.freeze([
  'Capa explícita',
  'Ordem persistida',
  'Alt text por foto',
  'Direitos de uso registrados',
  'Preflight sem erro',
  'QA aprovado antes de publicar',
]);
const scrollContainer = ref(null);
const formText = reactive({
  midias: '',
  caracteristicas: '',
});
const persistedAdditionalAttributes = ref({});
const addressMeta = reactive({
  google_place_id: '',
  endereco_completo: '',
  address_source: '',
  numero: '',
  sem_numero: false,
  complemento: '',
  andar: '',
});
const featureForm = reactive({
  aceita_animais: '',
  cobertura: '',
  suites: null,
  ar_condicionado: '',
  piscina: '',
  academia_condominio: '',
  metro_proximo: '',
  mobiliado: '',
  has_iptu: 'sim',
  itens_mobiliados: [],
});
const ownerForm = reactive({
  nome: '',
  telefone: '',
  email: '',
  perfil: '',
});
const pricingForm = reactive({
  faster_suggestion: null,
  recommended_price: null,
  max_suggestion: null,
  prediction_certainty: '',
  report_link: '',
  report_agent_name: '',
  report_company: 'Imudei',
});
const taxFeeForm = reactive({
  valor_condominio: {
    source_kind: 'unknown',
    status: 'unknown',
    confidence: 'unknown',
    origin_date: '',
  },
  valor_iptu: {
    source_kind: 'unknown',
    status: 'unknown',
    confidence: 'unknown',
    origin_date: '',
  },
});
const priceEstimate = ref(null);
const priceEstimateRequestedAt = ref('');
const priceEstimateLoading = ref(false);
const priceEstimateError = ref('');
const priceApplyReason = ref('');
const pendingPriceApplication = ref(null);
const isApplyEstimateModalOpen = ref(false);
const pendingPriceEstimateConfirmation = ref(null);
const existingContractStatus = ref('');
const listingReviewForm = reactive({
  doorman: '',
  condominium_amenities: [],
  property_amenities: [],
  notes: '',
});
const operationForm = reactive({
  rent_exclusivity: '',
  photo_session_status: '',
  photo_session_date: '',
  rent_visit_priority: '',
  sale_visit_priority: '',
  entry_access_type: '',
  visit_notes: '',
});

const form = reactive({
  external_id: '',
  titulo: '',
  descricao: '',
  tipo: 'apartamento',
  finalidade: 'venda',
  status: 'disponivel',
  valor_venda: null,
  valor_aluguel: null,
  valor_iptu: null,
  valor_condominio: null,
  quartos: null,
  banheiros: null,
  vagas_garagem: null,
  area_util: null,
  area_total: null,
  endereco: '',
  bairro: '',
  cidade: '',
  uf: '',
  cep: '',
  latitude: null,
  longitude: null,
  portal_origem: '',
  link_externo: '',
  midias: [],
  caracteristicas: {},
  oferece_financiamento: false,
});

const YES_NO_UNKNOWN_OPTIONS = [
  { value: '', label: 'Não sei' },
  { value: 'sim', label: 'Sim' },
  { value: 'nao', label: 'Não' },
];

const YES_NO_OPTIONS = [
  { value: '', label: 'Não sei' },
  { value: 'sim', label: 'Sim' },
  { value: 'nao', label: 'Não' },
];

const FURNITURE_OPTIONS = [
  { value: 'armario_cozinha', label: 'Armário na cozinha' },
  { value: 'fogao_cooktop', label: 'Fogão/cooktop' },
  { value: 'forno', label: 'Forno (pode ser do fogão)' },
  { value: 'geladeira', label: 'Geladeira' },
  { value: 'armario_quarto', label: 'Armário no quarto' },
  { value: 'microondas', label: 'Microondas' },
  { value: 'televisao', label: 'Televisão' },
];

const IPTU_OPTIONS = [
  { value: 'sim', label: 'Sim' },
  { value: 'nao', label: 'Não' },
];

const OWNER_PROFILE_OPTIONS = [
  { value: '', label: 'Não informado' },
  { value: 'proprietario', label: 'Proprietário' },
  { value: 'imobiliaria', label: 'Imobiliária parceira' },
  { value: 'corretor', label: 'Corretor responsável' },
  { value: 'inquilino_atual', label: 'Atual inquilino' },
  { value: 'outro', label: 'Outro' },
];

const PRICE_CERTAINTY_OPTIONS = [
  { value: '', label: 'Não informado' },
  { value: 'alta', label: 'Alta confiança' },
  { value: 'limitada', label: 'Dados limitados na região' },
];
const PRICE_ESTIMATE_SOURCE_FALLBACK = 'QPreço Imudei';
const PRICE_ESTIMATE_HISTORY_LIMIT = 10;
const PRICE_ESTIMATE_CONFIDENCE_LABELS = {
  high: 'Alta confiança',
  medium: 'Confiança média',
  low: 'Baixa confiança',
  insufficient_data: 'Sem comparáveis suficientes',
  alta: 'Alta confiança',
  limitada: 'Dados limitados na região',
};
const TAX_FEE_HISTORY_LIMIT = 10;
const TAX_FEE_FORM_FIELDS = [
  {
    field: 'valor_condominio',
    camelField: 'valorCondominio',
    label: 'Condomínio',
    period: 'mensal',
  },
  {
    field: 'valor_iptu',
    camelField: 'valorIptu',
    label: 'IPTU',
    period: 'anual',
  },
];
const TAX_FEE_SOURCE_OPTIONS = [
  { value: 'unknown', label: TAX_FEE_SOURCE_LABELS.unknown },
  { value: 'manual', label: TAX_FEE_SOURCE_LABELS.manual },
  { value: 'imported', label: TAX_FEE_SOURCE_LABELS.imported },
  { value: 'estimated', label: TAX_FEE_SOURCE_LABELS.estimated },
  { value: 'confirmed', label: TAX_FEE_SOURCE_LABELS.confirmed },
];
const TAX_FEE_CONFIDENCE_OPTIONS = [
  { value: 'unknown', label: TAX_FEE_CONFIDENCE_LABELS.unknown },
  { value: 'low', label: TAX_FEE_CONFIDENCE_LABELS.low },
  { value: 'medium', label: TAX_FEE_CONFIDENCE_LABELS.medium },
  { value: 'high', label: TAX_FEE_CONFIDENCE_LABELS.high },
  { value: 'confirmed', label: TAX_FEE_CONFIDENCE_LABELS.confirmed },
];

const DOORMAN_OPTIONS = [
  { value: '', label: 'Não informado' },
  { value: 'NaoHaPorteiro', label: 'Sem portaria' },
  { value: 'Diurno', label: 'Diurna' },
  { value: 'Noturno', label: 'Noturna' },
  { value: 'horas24', label: '24hrs' },
];

const CONDOMINIUM_AMENITIES = [
  { value: 'ACADEMIA', label: 'Academia' },
  { value: 'AREA_VERDE', label: 'Área verde' },
  { value: 'BRINQUEDOTECA', label: 'Brinquedoteca' },
  { value: 'CHURRASQUEIRA', label: 'Churrasqueira' },
  { value: 'ELEVADOR', label: 'Elevador' },
  { value: 'LAVANDERIA_NO_PREDIO', label: 'Lavanderia' },
  { value: 'PISCINA', label: 'Piscina' },
  { value: 'PLAYGROUND', label: 'Playground' },
  { value: 'PORTARIA', label: 'Portaria' },
  { value: 'QUADRA_ESPORTIVA', label: 'Quadra esportiva' },
  { value: 'SALAO_DE_FESTAS', label: 'Salão de festas' },
  { value: 'SALAO_DE_JOGOS', label: 'Salão de jogos' },
  { value: 'SAUNA', label: 'Sauna' },
];

const PROPERTY_AMENITIES = [
  { value: 'APARTAMENTO_COBERTURA', label: 'Apartamento cobertura' },
  { value: 'AR_CONDICIONADO', label: 'Ar condicionado' },
  { value: 'BANHEIRA_DE_HIDROMASSAGEM', label: 'Banheira' },
  { value: 'CHUVEIRO_A_GAS', label: 'Chuveiro a gás' },
  { value: 'CLOSET', label: 'Closet' },
  { value: 'PISCINA_PRIVATIVA', label: 'Piscina privativa' },
  { value: 'TANQUE', label: 'Tanque' },
  { value: 'VENTILADOR_DE_TETO', label: 'Ventilador de teto' },
  { value: 'VARANDA_GOURMET', label: 'Churrasqueira privativa' },
];

const EXCLUSIVITY_OPTIONS = [
  { value: '', label: 'Não informado' },
  { value: 'exclusivo_imudei', label: 'Exclusivo com a Imudei' },
  { value: 'nao_exclusivo', label: 'Não exclusivo' },
  { value: 'em_negociacao', label: 'Em negociação' },
];

const PHOTO_SESSION_OPTIONS = [
  { value: '', label: 'Não informado' },
  { value: 'agendar', label: 'Agendar a sessão de fotos' },
  { value: 'agendada', label: 'Sessão de fotos agendada' },
  { value: 'concluida', label: 'Sessão de fotos concluída' },
  { value: 'dispensada', label: 'Não precisa de sessão de fotos' },
];

const VISIT_PRIORITY_OPTIONS = [
  { value: '', label: 'Não informado' },
  { value: 'alta', label: 'Prioridade alta' },
  { value: 'normal', label: 'Prioridade normal' },
  { value: 'baixa', label: 'Prioridade baixa' },
  { value: 'nao_receber', label: 'Não receber visitas' },
];

const ENTRY_ACCESS_OPTIONS = [
  { value: '', label: 'Não informado' },
  { value: 'password', label: 'Entrada com senha' },
  { value: 'keys_on_entrance', label: 'Chaves na portaria' },
  { value: 'lockbox', label: 'Caixinha porta-chaves Imudei (Lockbox)' },
  { value: 'locker', label: 'Chaves no escritório Imudei (HUB)' },
  { value: 'keys_with_agent', label: 'Chaves com corretor' },
  { value: 'owner_present', label: 'Chaves com o proprietário' },
  { value: 'tenant_living', label: 'Chaves com o atual inquilino' },
  { value: 'external_responsible', label: 'Chaves com outro responsável' },
  { value: 'partner', label: 'Chaves na imobiliária parceira' },
];

const HOUSE_REGISTRATION_COPY = Object.freeze({
  houseRegistration: 'Cadastro de Imóvel',
  form: 'Componente do formulário',
  location: 'Localização',
  propertyDetails: 'Dados do imóvel',
  ownerInformation: 'Responsável pelo imóvel',
  pricing: 'Valores e taxas',
  rentPricing: 'Valor de aluguel',
  rentExclusivity: 'Exclusividade',
  salePricing: 'Valor de venda',
  photoScheduling: 'Agendamento de fotos',
  rentVisitPriority: 'Visitas de aluguel',
  saleVisitPriority: 'Visitas de venda',
  reviewStepDescription: 'Revise as informações',
  unavailableRegionTitle: 'Imóvel fora da área de atuação',
  unavailableRegionMessage:
    'Este endereço está fora das regiões de atuação da Imudei e não poderá ser cadastrado.',
  errorDuplicatedHouseMessage: t('PROPERTIES.FORM.DUPLICATE_CHECK_ERROR'),
  errorSnackbarMessage:
    'Tivemos um problema ao enviar as informações. Por favor, tente novamente.',
  loadingPhotosMessage: 'Agendando a sessão de fotos...',
  loadingHouseRegistrationMessage: 'Enviando cadastro do imóvel...',
  loadingCompleted: 'Pronto!',
});

const REGISTRATION_STEPS = [
  { value: 'location', label: HOUSE_REGISTRATION_COPY.location },
  { value: 'propertyDetails', label: HOUSE_REGISTRATION_COPY.propertyDetails },
  {
    value: 'ownerInformation',
    label: HOUSE_REGISTRATION_COPY.ownerInformation,
  },
  { value: 'pricing', label: HOUSE_REGISTRATION_COPY.pricing },
  { value: 'rentPricing', label: HOUSE_REGISTRATION_COPY.rentPricing },
  { value: 'rentExclusivity', label: HOUSE_REGISTRATION_COPY.rentExclusivity },
  { value: 'salePricing', label: HOUSE_REGISTRATION_COPY.salePricing },
  { value: 'photoScheduling', label: HOUSE_REGISTRATION_COPY.photoScheduling },
  {
    value: 'rentVisitPriority',
    label: HOUSE_REGISTRATION_COPY.rentVisitPriority,
  },
  {
    value: 'saleVisitPriority',
    label: HOUSE_REGISTRATION_COPY.saleVisitPriority,
  },
];

const PORTAIS = [
  { value: '', label: 'Não informado' },
  { value: 'site_proprio', label: 'Site próprio' },
  { value: 'imovelweb', label: 'ImovelWeb' },
  { value: 'plug_imoveis', label: 'Plug Imóveis' },
  { value: 'chaves_na_mao', label: 'Chaves na Mão' },
  { value: 'df_imoveis', label: 'DF Imóveis' },
  { value: 'casa_mineira', label: 'Casa Mineira' },
  { value: 'casa_jau', label: 'Casa Jau' },
  { value: 'abc_imovel', label: 'ABC Imóvel' },
  { value: 'lead_4_sales', label: 'Lead 4 Sales' },
  { value: 'imoveis_sc', label: 'Imóveis-SC' },
  { value: 'sp_imovel', label: 'SP Imóvel' },
  { value: 'litoral_sp', label: 'Litoral SP Imóvel' },
  { value: 'guia_de_imoveis', label: 'Guia de Imóveis' },
  { value: 'outro', label: 'Outro portal' },
];

const HOUSE_FORM_MESSAGES = {
  areaRange: 'A área do imóvel deve ser maior que 0 e menor que 10.000 m²',
  integer: 'Apenas valores inteiros são permitidos',
  positiveValue: 'O valor informado deve ser maior ou igual a 0.',
  bathroomSuite:
    'O número de banheiros não pode ser menor que o número de suítes',
  submitError: HOUSE_REGISTRATION_COPY.errorSnackbarMessage,
  completed: HOUSE_REGISTRATION_COPY.loadingCompleted,
};

const serializeMidias = midias =>
  Array.isArray(midias)
    ? midias
        .map(item => (typeof item === 'string' ? item : item?.url))
        .filter(Boolean)
        .join('\n')
    : '';

const serializeCaracteristicas = caracteristicas => {
  if (!caracteristicas || typeof caracteristicas !== 'object') return '';

  return Object.entries(caracteristicas)
    .map(([key, value]) => {
      const normalizedValue = Array.isArray(value) ? value.join(', ') : value;
      return `${key}: ${normalizedValue}`;
    })
    .join('\n');
};

const parseMidias = value =>
  value
    .split('\n')
    .map(item => item.trim())
    .filter(Boolean);

const parseCaracteristicas = value =>
  value
    .split('\n')
    .map(item => item.trim())
    .filter(Boolean)
    .reduce((accumulator, line) => {
      const separatorIndex = line.indexOf(':');
      if (separatorIndex === -1) {
        accumulator[line] = 'Sim';
        return accumulator;
      }

      const key = line.slice(0, separatorIndex).trim();
      const parsedValue = line.slice(separatorIndex + 1).trim();

      if (key) {
        accumulator[key] = parsedValue || 'Sim';
      }

      return accumulator;
    }, {});

const arrayValue = value => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    return value
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  }
  return [];
};

const isBlankValue = value =>
  value === '' || value === null || value === undefined;

const numberValue = value => (isBlankValue(value) ? null : Number(value));

const formatCurrency = value => {
  const normalized = Number(value);

  if (!Number.isFinite(normalized) || normalized <= 0) {
    return 'Não informado';
  }

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(normalized);
};

const applyEstimateConfirmationDescription = computed(() => {
  const confirmation = pendingPriceEstimateConfirmation.value;

  if (!confirmation?.nextPrice) {
    return 'Isso substituirá o valor atual e registrará a aplicação no histórico/auditoria.';
  }

  const targetLabel =
    confirmation.targetField === 'valor_aluguel' ? 'aluguel' : 'valor de venda';

  return `Aplicar ${formatCurrency(confirmation.nextPrice)} como ${targetLabel}? Isso substituirá o valor atual e registrará a aplicação no histórico/auditoria.`;
});

const positiveNumber = value => {
  const normalized = Number(value);
  return Number.isFinite(normalized) && normalized > 0 ? normalized : null;
};

const formatDateTimeLabel = value => {
  if (!value) return 'Não solicitada';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Não solicitada';

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatOriginDateLabel = value =>
  value ? formatDateTimeLabel(value) : 'Origem sem data';

const previousTaxFeeGovernance = () => {
  const attrs = persistedAdditionalAttributes.value || {};
  return attrs.tax_fee_governance || attrs.taxFeeGovernance || {};
};

const taxFeeEntryFor = field => {
  const governance = previousTaxFeeGovernance();
  const config = TAX_FEE_FORM_FIELDS.find(item => item.field === field);
  return governance[field] || governance[config?.camelField] || {};
};

const currentTaxFeeValue = field => {
  if (field === 'valor_iptu' && featureForm.has_iptu === 'nao') return null;
  return normalizeOptionalNumber(form[field]);
};

const taxFeeSourceKind = (field, value) => {
  const selected = normalizeOptionalText(taxFeeForm[field].source_kind);
  if (selected && selected !== 'unknown') return selected;
  return value === null ? 'unknown' : 'manual';
};

const taxFeeStatus = (field, sourceKind) => {
  const selected = normalizeOptionalText(taxFeeForm[field].status);
  if (selected && selected !== 'unknown') return selected;
  return sourceKind;
};

const buildTaxFeeGovernance = () =>
  TAX_FEE_FORM_FIELDS.reduce((accumulator, config) => {
    const { field, label, period } = config;
    const previousEntry = taxFeeEntryFor(field);
    const canonicalValue = currentTaxFeeValue(field);
    const sourceKind = taxFeeSourceKind(field, canonicalValue);
    const status = taxFeeStatus(field, sourceKind);
    const confidence =
      normalizeOptionalText(taxFeeForm[field].confidence) ||
      previousEntry.confidence ||
      'unknown';
    const fallbackValue = normalizeOptionalNumber(previousEntry.fallback_value);
    const conflict =
      fallbackValue !== null &&
      canonicalValue !== null &&
      Number(fallbackValue) !== Number(canonicalValue);

    accumulator[field] = cleanDeepObject({
      ...previousEntry,
      field,
      label,
      period,
      canonical_value: canonicalValue,
      canonical_source: sourceKind,
      source_kind: sourceKind,
      source_label: TAX_FEE_SOURCE_LABELS[sourceKind],
      status,
      confidence,
      confidence_label: TAX_FEE_CONFIDENCE_LABELS[confidence],
      origin_date:
        normalizeOptionalText(taxFeeForm[field].origin_date) ||
        previousEntry.origin_date ||
        previousEntry.observed_at,
      fallback_value: previousEntry.fallback_value,
      fallback_source: previousEntry.fallback_source,
      fallback_source_label: previousEntry.fallback_source_label,
      conflict: conflict || undefined,
      history: Array(previousEntry.history).slice(-TAX_FEE_HISTORY_LIMIT),
    });
    return accumulator;
  }, {});

const taxFeeGovernanceRows = computed(() =>
  buildTaxFeeGovernanceRows({
    valor_condominio: form.valor_condominio,
    valor_iptu: featureForm.has_iptu === 'nao' ? null : form.valor_iptu,
    additional_attributes: {
      tax_fee_governance: buildTaxFeeGovernance(),
    },
  })
);

const compactDifferencePercent = (nextPrice, previousPrice) => {
  if (!previousPrice) return null;

  const percent = ((nextPrice - previousPrice) / previousPrice) * 100;
  return Number(percent.toFixed(2));
};

const isSaleContext = computed(() => form.finalidade === 'venda');
const isRentContext = computed(() =>
  ['aluguel', 'temporada'].includes(form.finalidade)
);
const shouldShowIptuValue = computed(() => featureForm.has_iptu !== 'nao');
const currentBusinessPrice = computed(() =>
  numberValue(isRentContext.value ? form.valor_aluguel : form.valor_venda)
);
const fasterSuggestionValue = computed(() =>
  numberValue(pricingForm.faster_suggestion)
);
const recommendedPriceValue = computed(() =>
  numberValue(pricingForm.recommended_price)
);
const maxSuggestionValue = computed(() =>
  numberValue(pricingForm.max_suggestion)
);
const chosenPriceLabel = computed(() =>
  currentBusinessPrice.value &&
  recommendedPriceValue.value &&
  currentBusinessPrice.value === recommendedPriceValue.value
    ? 'Valor sugerido'
    : 'Valor definido'
);
const internalNegotiatedTitle = computed(() =>
  isSaleContext.value ? 'Vendidos na Imudei' : 'Alugados na Imudei'
);
const internalNegotiatedStatus = computed(() =>
  isSaleContext.value ? 'Vendido' : 'Alugado'
);
const priceGuidance = computed(() => {
  const current = currentBusinessPrice.value;
  const faster = fasterSuggestionValue.value;
  const max = maxSuggestionValue.value;

  if (!current || !max) return null;
  if (current > max) {
    return {
      kind: 'warning',
      title: 'O valor está acima do valor máximo sugerido',
      message: `Imóveis como este costumam atrair menos interessados quando têm preço acima de ${formatCurrency(max)}.`,
    };
  }
  if (faster && current >= faster && current <= max) {
    return {
      kind: 'positive',
      title: 'Excelente',
      message: 'Preços dentro da faixa recomendada geram mais visitas',
    };
  }
  return null;
});
const priceEstimateSource = computed(
  () =>
    priceEstimate.value?.source ||
    priceEstimate.value?.fonte ||
    PRICE_ESTIMATE_SOURCE_FALLBACK
);
const priceEstimateConfidence = computed(
  () =>
    priceEstimate.value?.confidence ||
    priceEstimate.value?.confidence_level ||
    priceEstimate.value?.confidenceLevel ||
    'insufficient_data'
);
const priceEstimateConfidenceLabel = computed(
  () =>
    PRICE_ESTIMATE_CONFIDENCE_LABELS[priceEstimateConfidence.value] ||
    priceEstimateConfidence.value ||
    'Não informada'
);
const priceEstimateSamples = computed(() => {
  const value =
    priceEstimate.value?.samples ??
    priceEstimate.value?.comparables_count ??
    priceEstimate.value?.comparablesCount ??
    priceEstimate.value?.comparables?.length ??
    0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
});
const estimatedPriceValue = computed(() =>
  positiveNumber(
    priceEstimate.value?.estimated_price ??
      priceEstimate.value?.estimatedPrice ??
      priceEstimate.value?.recommended_price ??
      priceEstimate.value?.recommendedPrice ??
      priceEstimate.value?.median_price ??
      priceEstimate.value?.medianPrice
  )
);
const priceEstimateRangeLow = computed(() =>
  positiveNumber(
    priceEstimate.value?.price_range_low ??
      priceEstimate.value?.priceRangeLow ??
      priceEstimate.value?.price_p25 ??
      priceEstimate.value?.priceP25
  )
);
const priceEstimateRangeHigh = computed(() =>
  positiveNumber(
    priceEstimate.value?.price_range_high ??
      priceEstimate.value?.priceRangeHigh ??
      priceEstimate.value?.price_p75 ??
      priceEstimate.value?.priceP75
  )
);
const priceEstimateRangeLabel = computed(() => {
  if (priceEstimateRangeLow.value && priceEstimateRangeHigh.value) {
    return `${formatCurrency(priceEstimateRangeLow.value)} a ${formatCurrency(
      priceEstimateRangeHigh.value
    )}`;
  }

  return 'Faixa indisponível';
});
const priceEstimateDateLabel = computed(() =>
  formatDateTimeLabel(
    priceEstimate.value?.generated_at ||
      priceEstimate.value?.generatedAt ||
      priceEstimateRequestedAt.value
  )
);
const priceEstimateEmptyMessage = computed(
  () =>
    priceEstimate.value?.message ||
    'Sem comparáveis suficientes para esta combinação de bairro, tipo e área.'
);
const hasApplicablePriceEstimate = computed(() =>
  Boolean(estimatedPriceValue.value)
);
const persistedPriceApplications = computed(() => {
  const attrs = persistedAdditionalAttributes.value || {};
  const records =
    attrs.price_estimate_applications || attrs.priceEstimateApplications || [];
  return Array.isArray(records) ? records : [];
});
const priceApplicationHistory = computed(() => {
  const records = [...persistedPriceApplications.value];
  if (pendingPriceApplication.value) {
    records.push(pendingPriceApplication.value);
  }
  return records.slice(-PRICE_ESTIMATE_HISTORY_LIMIT).reverse();
});
const reportShareMessage = computed(() => {
  const agentName = pricingForm.report_agent_name || 'time Imudei';
  const company = pricingForm.report_company || 'Imudei';
  const value = formatCurrency(
    currentBusinessPrice.value || recommendedPriceValue.value || 0
  );
  const link = pricingForm.report_link || '';

  return [
    `Olá, proprietário! Aqui é ${agentName}, da ${company}, tudo bem?`,
    `Preparei essa análise do melhor valor para o seu imóvel com base no que temos visto no mercado imobiliário da região. Levando em conta as características dele, o preço recomendado é de ${value}.`,
    `Neste link tem mais detalhes: ${link}`,
  ].join('\n\n');
});

function composedAddressForPersistence() {
  const street = form.endereco?.trim() || '';
  const number = addressMeta.sem_numero ? '' : addressMeta.numero?.trim() || '';

  if (!street) return '';
  if (!number || street.includes(number)) return street;

  return `${street}, ${number}`;
}

const operationContextLabel = computed(() => {
  if (form.finalidade === 'venda') return 'venda';
  if (isRentContext.value) return 'aluguel';
  return 'aluguel e venda';
});

const savedPropertyId = computed(
  () => completion.value?.record?.id || route.params.id || ''
);

const savedPropertyUrl = computed(() => {
  if (!savedPropertyId.value) return window.location.href;

  return `${window.location.origin}${frontendURL(
    `accounts/${accountId.value}/imoveis/${savedPropertyId.value}`
  )}`;
});

const ownerConfirmationLink = computed(
  () => pricingForm.report_link || form.link_externo || savedPropertyUrl.value
);

const ownerConfirmationAddress = computed(() =>
  [composedAddressForPersistence(), form.bairro, form.cidade, form.uf]
    .filter(Boolean)
    .join(', ')
);

const ownerConfirmationMessage = computed(() =>
  [
    `Olá, ${ownerForm.nome || 'proprietário'}!`,
    `Pré-cadastrei seu imóvel para ${operationContextLabel.value}, localizado em:`,
    ownerConfirmationAddress.value || 'Endereço não informado',
    '*Para confirmar o cadastro do imóvel, clique no link a seguir*',
    ownerConfirmationLink.value,
  ].join('\n\n')
);

const formatSuccessDate = date =>
  date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
  });

const formatSuccessWeekdayDate = date =>
  date.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  });

const successPhotoDate = computed(() => {
  if (!operationForm.photo_session_date) return null;

  const date = new Date(operationForm.photo_session_date);
  return Number.isNaN(date.getTime()) ? null : date;
});

const successPhotoDateText = computed(() => {
  if (!successPhotoDate.value) return '';

  return `${formatSuccessWeekdayDate(
    successPhotoDate.value
  )}, às ${successPhotoDate.value.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
});

const listingPublicationText = computed(() => {
  const baseDate = successPhotoDate.value || new Date();
  const publicationDate = new Date(baseDate);
  publicationDate.setDate(publicationDate.getDate() + 7);

  return `Até ${formatSuccessDate(publicationDate)}, seu anúncio vai estar pronto para agendamentos de visitas.`;
});

const successSteps = computed(() =>
  [
    {
      icon: 'i-lucide-user-check',
      title: 'Aguarde a confirmação do proprietário',
      description:
        'Enviamos uma mensagem ao proprietário para confirmar o cadastro do imóvel.',
    },
    {
      icon: 'i-lucide-camera',
      title: 'Agende a sessão de fotos',
      description:
        'Após a confirmação do proprietário, você precisará agendar a sessão de fotos.',
    },
    successPhotoDateText.value
      ? {
          icon: 'i-lucide-calendar-check',
          title: 'Data e horário das fotos',
          description: successPhotoDateText.value,
        }
      : null,
    {
      icon: 'i-lucide-megaphone',
      title: 'Publicação do anúncio',
      description: listingPublicationText.value,
    },
  ].filter(Boolean)
);

const assistantSuccessBullets = [
  'Agora, a assistente vai iniciar uma conversa com o proprietário pelo WhatsApp.',
  'Se o proprietário aceitar anunciar o imóvel, avisamos você para terminar o cadastro.',
  'Também avisamos se não for possível anunciar o imóvel, ou se o proprietário prefere conversar diretamente com você.',
];

const keyAccessCards = [
  {
    icon: 'i-lucide-package',
    action: 'lockbox',
    title: 'Caixinha porta-chaves Imudei (Lockbox)',
    description:
      'As chaves ficam disponíveis para o parceiro no porta-chaves da Imudei.',
    button: 'Solicitar o lockbox',
  },
  {
    icon: 'i-lucide-key-round',
    action: 'agent_keys',
    title: 'Chaves com corretor',
    description:
      'Um corretor parceiro fica responsável pelas chaves e todas as visitas.',
    button: 'Como funciona o corretor com chaves',
  },
  {
    icon: 'i-lucide-building-2',
    action: 'entrance_keys',
    title: 'Chaves na portaria',
    description:
      'As chaves ficam na portaria, junto com a autorização de entrada dos parceiros.',
    button: 'Como funciona o corretor com chaves',
  },
];

const isIntegerValue = value => {
  if (isBlankValue(value)) return true;

  const normalized = Number(value);
  return Number.isInteger(normalized);
};

const isPositiveArea = value => {
  if (isBlankValue(value)) return true;

  const normalized = Number(value);
  return Number.isInteger(normalized) && normalized > 0 && normalized < 10000;
};

const isNonNegativeValue = value => {
  if (isBlankValue(value)) return true;

  const normalized = Number(value);
  return !Number.isNaN(normalized) && normalized >= 0;
};

const addressModel = computed({
  get: () => ({
    endereco: form.endereco,
    numero: addressMeta.numero,
    sem_numero: addressMeta.sem_numero,
    bairro: form.bairro,
    cidade: form.cidade,
    uf: form.uf,
    cep: form.cep,
    latitude: form.latitude,
    longitude: form.longitude,
    google_place_id: addressMeta.google_place_id,
    endereco_completo: addressMeta.endereco_completo,
    address_source: addressMeta.address_source,
  }),
  set: value => {
    form.endereco = value?.endereco || '';
    addressMeta.numero = value?.numero || '';
    addressMeta.sem_numero = Boolean(value?.sem_numero);
    form.bairro = value?.bairro || '';
    form.cidade = value?.cidade || '';
    form.uf = value?.uf || '';
    form.cep = value?.cep || '';
    form.latitude = value?.latitude ?? null;
    form.longitude = value?.longitude ?? null;
    addressMeta.google_place_id = value?.google_place_id || '';
    addressMeta.endereco_completo = value?.endereco_completo || '';
    addressMeta.address_source = value?.address_source || '';
  },
});

const priceEstimatePayload = () =>
  cleanObject({
    bairro: normalizeOptionalText(form.bairro),
    cidade: normalizeOptionalText(form.cidade),
    tipo: normalizeOptionalText(form.tipo),
    finalidade: normalizeOptionalText(form.finalidade),
    area_util: normalizeOptionalNumber(form.area_util),
    quartos: normalizeOptionalNumber(form.quartos),
    window_days: 180,
  });

const priceEstimateErrorMessage = error =>
  error?.response?.data?.errors?.[0] ||
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  'Não foi possível estimar preço agora. Você pode salvar o imóvel normalmente.';

const solicitarEstimativaPreco = async () => {
  priceEstimateLoading.value = true;
  priceEstimateError.value = '';
  try {
    const { data } = await ImovelAPI.estimatePrice(priceEstimatePayload());
    priceEstimate.value = data || {};
    priceEstimateRequestedAt.value = new Date().toISOString();

    if (!hasApplicablePriceEstimate.value) {
      useAlert(priceEstimateEmptyMessage.value);
    }
  } catch (error) {
    priceEstimateError.value = priceEstimateErrorMessage(error);
    useAlert(priceEstimateError.value);
  } finally {
    priceEstimateLoading.value = false;
  }
};

const normalizedPricingCertainty = () =>
  priceEstimateConfidence.value === 'high' ? 'alta' : 'limitada';

const buildPriceEstimateApplication = confirmation => {
  const appliedPrice =
    confirmation?.nextPrice ||
    confirmation?.appliedPrice ||
    estimatedPriceValue.value;
  const previousPrice =
    confirmation?.previousPrice !== undefined
      ? confirmation.previousPrice
      : currentBusinessPrice.value;
  const targetField =
    confirmation?.targetField ||
    (isRentContext.value ? 'valor_aluguel' : 'valor_venda');
  const finalidade = confirmation?.finalidade || form.finalidade;

  return cleanDeepObject({
    source: priceEstimateSource.value,
    reason: normalizeOptionalText(priceApplyReason.value),
    target_field: targetField,
    finalidade,
    previous_price: previousPrice,
    applied_price: appliedPrice,
    estimated_price: appliedPrice,
    difference_value:
      previousPrice === null
        ? null
        : Number((appliedPrice - previousPrice).toFixed(2)),
    difference_percent: compactDifferencePercent(appliedPrice, previousPrice),
    confidence: priceEstimateConfidence.value,
    samples: priceEstimateSamples.value,
    price_range_low: priceEstimateRangeLow.value,
    price_range_high: priceEstimateRangeHigh.value,
    estimate_requested_at: priceEstimateRequestedAt.value,
    confirmed_at: new Date().toISOString(),
    existing_contract: Boolean(existingContractStatus.value),
    contract_status: existingContractStatus.value,
    estimate_payload: priceEstimate.value,
  });
};

const mergePriceEstimateApplications = previous => {
  const existing = Array(
    previous.price_estimate_applications || previous.priceEstimateApplications
  )
    .flat()
    .filter(Boolean);

  if (!pendingPriceApplication.value) {
    return existing.slice(-PRICE_ESTIMATE_HISTORY_LIMIT);
  }

  const alreadyPersisted = existing.some(
    entry => entry.confirmed_at === pendingPriceApplication.value.confirmed_at
  );
  const next = alreadyPersisted
    ? existing
    : [...existing, pendingPriceApplication.value];

  return next.slice(-PRICE_ESTIMATE_HISTORY_LIMIT);
};

const aplicarEstimativaPreco = () => {
  if (!hasApplicablePriceEstimate.value) {
    useAlert('Solicite uma estimativa com comparáveis antes de aplicar.');
    return;
  }

  if (!normalizeOptionalText(priceApplyReason.value)) {
    useAlert('Informe o motivo para aplicar a estimativa.');
    return;
  }

  pendingPriceEstimateConfirmation.value = {
    nextPrice: estimatedPriceValue.value,
    previousPrice: currentBusinessPrice.value,
    targetField: isRentContext.value ? 'valor_aluguel' : 'valor_venda',
    finalidade: form.finalidade,
  };
  isApplyEstimateModalOpen.value = true;
};

const fecharModalAplicarEstimativa = () => {
  isApplyEstimateModalOpen.value = false;
  pendingPriceEstimateConfirmation.value = null;
};

const confirmarAplicacaoEstimativa = () => {
  const confirmation = pendingPriceEstimateConfirmation.value;

  if (!confirmation?.nextPrice) {
    fecharModalAplicarEstimativa();
    return;
  }

  if (confirmation.targetField === 'valor_aluguel') {
    form.valor_aluguel = confirmation.nextPrice;
  } else {
    form.valor_venda = confirmation.nextPrice;
  }
  pricingForm.faster_suggestion =
    priceEstimateRangeLow.value || pricingForm.faster_suggestion;
  pricingForm.recommended_price = confirmation.nextPrice;
  pricingForm.max_suggestion =
    priceEstimateRangeHigh.value || pricingForm.max_suggestion;
  pricingForm.prediction_certainty = normalizedPricingCertainty();
  pendingPriceApplication.value = buildPriceEstimateApplication(confirmation);
  fecharModalAplicarEstimativa();
  useAlert(
    'Estimativa aplicada no formulário. Salve para persistir o histórico.'
  );
};

const buildCaracteristicas = () =>
  cleanObject({
    ...parseCaracteristicas(formText.caracteristicas),
    aceita_animais: normalizeOptionalText(featureForm.aceita_animais),
    cobertura: normalizeOptionalText(featureForm.cobertura),
    suites: normalizeOptionalNumber(featureForm.suites),
    ar_condicionado: normalizeOptionalText(featureForm.ar_condicionado),
    piscina: normalizeOptionalText(featureForm.piscina),
    academia_condominio: normalizeOptionalText(featureForm.academia_condominio),
    metro_proximo: normalizeOptionalText(featureForm.metro_proximo),
    mobiliado: normalizeOptionalText(featureForm.mobiliado),
    has_iptu: normalizeOptionalText(featureForm.has_iptu),
    itens_mobiliados: featureForm.itens_mobiliados,
  });

const buildAdditionalAttributes = () => {
  const previous = persistedAdditionalAttributes.value || {};
  const priceEstimateApplications = mergePriceEstimateApplications(previous);

  return cleanDeepObject({
    ...previous,
    google_place_id: normalizeOptionalText(addressMeta.google_place_id),
    full_address: normalizeOptionalText(addressMeta.endereco_completo),
    address_source: normalizeOptionalText(addressMeta.address_source),
    address_street: normalizeOptionalText(form.endereco),
    address_number: addressMeta.sem_numero
      ? 'Sem número'
      : normalizeOptionalText(addressMeta.numero),
    without_number: addressMeta.sem_numero || undefined,
    address_complement: normalizeOptionalText(addressMeta.complemento),
    floor: normalizeOptionalText(addressMeta.andar),
    owner_name: normalizeOptionalText(ownerForm.nome),
    owner_phone: normalizeOptionalText(ownerForm.telefone),
    owner_email: normalizeOptionalText(ownerForm.email),
    owner_profile: normalizeOptionalText(ownerForm.perfil),
    rent_exclusivity: normalizeOptionalText(operationForm.rent_exclusivity),
    photo_session_status: normalizeOptionalText(
      operationForm.photo_session_status
    ),
    photo_session_date: normalizeOptionalText(operationForm.photo_session_date),
    rent_visit_priority: normalizeOptionalText(
      operationForm.rent_visit_priority
    ),
    sale_visit_priority: normalizeOptionalText(
      operationForm.sale_visit_priority
    ),
    entry_access_type: normalizeOptionalText(operationForm.entry_access_type),
    visit_notes: normalizeOptionalText(operationForm.visit_notes),
    pricing_suggestions: {
      ...(previous.pricing_suggestions || {}),
      faster_suggestion: normalizeOptionalNumber(pricingForm.faster_suggestion),
      recommended_price: normalizeOptionalNumber(pricingForm.recommended_price),
      max_suggestion: normalizeOptionalNumber(pricingForm.max_suggestion),
      prediction_certainty: normalizeOptionalText(
        pricingForm.prediction_certainty
      ),
      report_link: normalizeOptionalText(pricingForm.report_link),
      report_agent_name: normalizeOptionalText(pricingForm.report_agent_name),
      report_company: normalizeOptionalText(pricingForm.report_company),
    },
    tax_fee_governance: buildTaxFeeGovernance(),
    price_estimate_application:
      pendingPriceApplication.value || previous.price_estimate_application,
    price_estimate_applications: priceEstimateApplications,
    listing_review: {
      ...(previous.listing_review || {}),
      doorman: normalizeOptionalText(listingReviewForm.doorman),
      condominium_amenities: listingReviewForm.condominium_amenities,
      property_amenities: listingReviewForm.property_amenities,
      notes: normalizeOptionalText(listingReviewForm.notes),
    },
    registration_flow_source: 'portfolio_house_registration',
    registration_flow_sections: REGISTRATION_STEPS.map(step => step.label),
    house_info_section_title: 'Características',
    listing_review_title: HOUSE_REGISTRATION_COPY.reviewStepDescription,
    auto_fill_card_title: 'Preenchemos automaticamente',
    auto_fill_card_description:
      'Confira os itens preenchidos pela nossa inteligência, vamos coletar mais dados na sessão de fotos.',
  });
};

const buildPayload = () =>
  normalizeImovelPayload(
    {
      ...form,
      endereco: composedAddressForPersistence(),
      external_id: form.external_id,
      valor_venda: isSaleContext.value ? form.valor_venda : null,
      valor_aluguel: isRentContext.value ? form.valor_aluguel : null,
      valor_iptu: featureForm.has_iptu === 'nao' ? null : form.valor_iptu,
      midias: parseMidias(formText.midias),
      caracteristicas: buildCaracteristicas(),
      additional_attributes: buildAdditionalAttributes(),
    },
    {
      owner: IMOVEL_FORM_OWNER.EDITOR,
      mode: isEditing ? 'edit' : 'create',
    }
  );

const validateForm = () => {
  if (!isPositiveArea(form.area_util)) {
    useAlert(HOUSE_FORM_MESSAGES.areaRange);
    return false;
  }

  const integerFields = [
    form.area_total,
    form.vagas_garagem,
    form.quartos,
    form.banheiros,
    featureForm.suites,
    form.valor_venda,
    form.valor_aluguel,
    form.valor_iptu,
    form.valor_condominio,
  ];

  if (integerFields.some(value => !isIntegerValue(value))) {
    useAlert(HOUSE_FORM_MESSAGES.integer);
    return false;
  }

  if (integerFields.some(value => !isNonNegativeValue(value))) {
    useAlert(HOUSE_FORM_MESSAGES.positiveValue);
    return false;
  }

  const bathrooms = numberValue(form.banheiros);
  const suites = numberValue(featureForm.suites);
  if (bathrooms !== null && suites !== null && bathrooms < suites) {
    useAlert(HOUSE_FORM_MESSAGES.bathroomSuite);
    return false;
  }

  return true;
};

const assignFormFields = data => {
  [
    'external_id',
    'titulo',
    'descricao',
    'tipo',
    'finalidade',
    'status',
    'valor_venda',
    'valor_aluguel',
    'valor_iptu',
    'valor_condominio',
    'quartos',
    'banheiros',
    'vagas_garagem',
    'area_util',
    'area_total',
    'endereco',
    'bairro',
    'cidade',
    'uf',
    'cep',
    'latitude',
    'longitude',
    'portal_origem',
    'link_externo',
    'oferece_financiamento',
    'midias',
    'caracteristicas',
  ].forEach(field => {
    if (Object.hasOwn(data, field)) {
      form[field] = data[field];
    }
  });
};

const assignTaxFeeGovernanceFields = data => {
  const attrs = data.additional_attributes || {};
  const governance =
    data.tax_fee_governance ||
    attrs.tax_fee_governance ||
    attrs.taxFeeGovernance ||
    {};

  TAX_FEE_FORM_FIELDS.forEach(({ field, camelField }) => {
    const entry = governance[field] || governance[camelField] || {};
    taxFeeForm[field].source_kind =
      entry.canonical_source || entry.source_kind || 'unknown';
    taxFeeForm[field].status =
      entry.status || entry.source_status || taxFeeForm[field].source_kind;
    taxFeeForm[field].confidence =
      entry.confidence || entry.canonical_confidence || 'unknown';
    taxFeeForm[field].origin_date = (
      entry.origin_date ||
      entry.observed_at ||
      entry.canonical_updated_at ||
      ''
    )
      .toString()
      .slice(0, 10);
  });
};

const assignFeatureFields = data => {
  const caracteristicas = data.caracteristicas || {};
  const attrs = data.additional_attributes || {};
  const pricingAttrs = attrs.pricing_suggestions || {};
  const reviewAttrs = attrs.listing_review || {};

  featureForm.aceita_animais =
    caracteristicas.aceita_animais || caracteristicas.pets || '';
  featureForm.cobertura =
    caracteristicas.cobertura || caracteristicas.penthouse || '';
  featureForm.suites = caracteristicas.suites || caracteristicas.suite || null;
  featureForm.ar_condicionado =
    caracteristicas.ar_condicionado || caracteristicas.ac || '';
  featureForm.piscina = caracteristicas.piscina || caracteristicas.pool || '';
  featureForm.academia_condominio =
    caracteristicas.academia_condominio || caracteristicas.gym || '';
  featureForm.metro_proximo =
    caracteristicas.metro_proximo || caracteristicas.near_subway || '';
  featureForm.mobiliado =
    caracteristicas.mobiliado || caracteristicas.is_furnished || '';
  featureForm.has_iptu =
    caracteristicas.has_iptu || (data.valor_iptu ? 'sim' : 'nao');
  featureForm.itens_mobiliados = arrayValue(
    caracteristicas.itens_mobiliados || caracteristicas.furniture
  );
  addressMeta.complemento =
    attrs.address_complement || attrs.complement || attrs.complemento || '';
  addressMeta.andar = attrs.floor || attrs.andar || '';
  addressMeta.numero =
    attrs.address_number === 'Sem número'
      ? ''
      : attrs.address_number || attrs.number || attrs.numero || '';
  addressMeta.sem_numero = Boolean(
    attrs.without_number ||
      attrs.sem_numero ||
      attrs.address_number === 'Sem número'
  );
  ownerForm.nome = attrs.owner_name || attrs.owner?.name || '';
  ownerForm.telefone = attrs.owner_phone || attrs.owner?.phone || '';
  ownerForm.email = attrs.owner_email || attrs.owner?.email || '';
  ownerForm.perfil = attrs.owner_profile || attrs.owner?.profile || '';
  pricingForm.faster_suggestion =
    pricingAttrs.faster_suggestion || attrs.faster_suggestion || null;
  pricingForm.recommended_price =
    pricingAttrs.recommended_price || attrs.recommended_price || null;
  pricingForm.max_suggestion =
    pricingAttrs.max_suggestion || attrs.max_suggestion || null;
  pricingForm.prediction_certainty =
    pricingAttrs.prediction_certainty || attrs.prediction_certainty || '';
  pricingForm.report_link = pricingAttrs.report_link || attrs.report_link || '';
  pricingForm.report_agent_name =
    pricingAttrs.report_agent_name || attrs.report_agent_name || '';
  pricingForm.report_company =
    pricingAttrs.report_company || attrs.report_company || 'Imudei';
  listingReviewForm.doorman = reviewAttrs.doorman || attrs.doorman || '';
  listingReviewForm.condominium_amenities = arrayValue(
    reviewAttrs.condominium_amenities || attrs.condominium_amenities
  );
  listingReviewForm.property_amenities = arrayValue(
    reviewAttrs.property_amenities || attrs.property_amenities
  );
  listingReviewForm.notes =
    reviewAttrs.notes || attrs.listing_review_notes || '';
  operationForm.rent_exclusivity =
    attrs.rent_exclusivity || attrs.exclusivity || '';
  operationForm.photo_session_status = attrs.photo_session_status || '';
  operationForm.photo_session_date = attrs.photo_session_date || '';
  operationForm.rent_visit_priority = attrs.rent_visit_priority || '';
  operationForm.sale_visit_priority = attrs.sale_visit_priority || '';
  operationForm.entry_access_type =
    attrs.entry_access_type || attrs.entryAccessType || '';
  operationForm.visit_notes = attrs.visit_notes || '';
  assignTaxFeeGovernanceFields(data);
};

const carregar = async () => {
  if (!isEditing) return;
  isLoading.value = true;
  loadError.value = false;
  try {
    await store.show(route.params.id);
    const data = store.records.find(r => r.id === Number(route.params.id));
    if (data) {
      persistedAdditionalAttributes.value = data.additional_attributes || {};
      assignFormFields(data);
      formText.midias = serializeMidias(data.midias);
      formText.caracteristicas = serializeCaracteristicas(data.caracteristicas);
      addressMeta.google_place_id =
        data.additional_attributes?.google_place_id || '';
      addressMeta.endereco_completo =
        data.additional_attributes?.full_address || '';
      addressMeta.address_source =
        data.additional_attributes?.address_source || '';
      existingContractStatus.value =
        data.contract_status ||
        data.additional_attributes?.contract_status ||
        data.additional_attributes?.contractStatus ||
        '';
      assignFeatureFields(data);
    } else {
      loadError.value = true;
    }
  } catch {
    loadError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const salvar = async () => {
  if (!validateForm()) return;

  isSaving.value = true;
  try {
    const payload = buildPayload();
    let record;
    if (isEditing) {
      record = await store.update({ id: route.params.id, ...payload });
      useAlert('Prontinho! Estamos preparando seu anúncio');
    } else {
      record = await store.create(payload);
      useAlert(HOUSE_FORM_MESSAGES.completed);
    }
    completion.value = {
      record,
      mode: isEditing ? 'edit' : 'create',
    };
    persistedAdditionalAttributes.value =
      record?.additional_attributes || persistedAdditionalAttributes.value;
    fecharModalAplicarEstimativa();
    pendingPriceApplication.value = null;
    await nextTick();
    scrollContainer.value?.scrollTo({ top: 0, behavior: 'smooth' });
  } catch {
    useAlert(HOUSE_FORM_MESSAGES.submitError);
  } finally {
    isSaving.value = false;
  }
};

const cancelar = () =>
  router.push(frontendURL(`accounts/${accountId.value}/imoveis`));

const compartilharRelatorio = () => {
  if (!pricingForm.report_link) {
    useAlert('O link abaixo pode ser copiado e enviado');
    return;
  }

  window.open(
    `https://wa.me/?text=${encodeURIComponent(reportShareMessage.value)}`,
    '_blank',
    'noopener,noreferrer'
  );
};

const copiarLinkConfirmacao = async () => {
  try {
    await navigator.clipboard.writeText(ownerConfirmationLink.value);
    useAlert('Link copiado com sucesso!');
  } catch {
    useAlert('Não foi possível copiar o link de confirmação.');
  }
};

const enviarLinkConfirmacaoWhatsapp = () => {
  window.open(
    `https://wa.me/?text=${encodeURIComponent(ownerConfirmationMessage.value)}`,
    '_blank',
    'noopener,noreferrer'
  );
};

const cadastrarOutroImovel = () => {
  window.location.assign(
    frontendURL(`accounts/${accountId.value}/imoveis/new`)
  );
};

const irParaRascunhos = () =>
  router.push(frontendURL(`accounts/${accountId.value}/imoveis`));

const irParaCriarImovel = () =>
  router.push(frontendURL(`accounts/${accountId.value}/criar-imovel`));

// FE-AUD-19: sucesso de criação leva para o detalhe canônico, onde vive o
// checklist de completude/publicabilidade.
const verImovelCriado = () => {
  if (!savedPropertyId.value) {
    irParaRascunhos();
    return;
  }

  router.push(
    frontendURL(`accounts/${accountId.value}/imoveis/${savedPropertyId.value}`)
  );
};

const irParaEditorCompleto = () => {
  if (!savedPropertyId.value) {
    activeFormTab.value = 'anuncio';
    return;
  }

  router.push(
    frontendURL(
      `accounts/${accountId.value}/imoveis/${savedPropertyId.value}/edit`
    )
  );
};

const abrirSecaoOperacional = link => {
  activeFormTab.value = 'anuncio';
  nextTick(() => {
    document
      .getElementById(link.target)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
};

const solicitarLockbox = () => {
  useAlert(
    'Basta preencher o formulário e encaminharemos o lockbox para o endereço de escolha informado. A instalação do lockbox é de responsabilidade do proprietário.'
  );
};

const abrirGuiaCorretorComChaves = () => {
  useAlert(
    'Selecionaremos um corretor dedicado para cuidar das visitas, e na primeira visita, o proprietário deverá entregar uma cópia das chaves ao corretor.'
  );
};

const handleKeyAccessAction = card => {
  if (card.action === 'lockbox') {
    solicitarLockbox();
    return;
  }

  abrirGuiaCorretorComChaves();
};

watch(
  () => featureForm.has_iptu,
  value => {
    if (value === 'nao') form.valor_iptu = null;
  }
);

onMounted(carregar);

const input =
  'w-full px-3 py-2.5 text-[14px] text-lais-text bg-white border border-lais-border rounded-lais-sm focus:outline-none focus:border-lais-primary focus:ring-2 focus:ring-lais-primary-50 placeholder:text-lais-text-muted transition-all';
const sectionGrid = 'grid grid-cols-1 gap-4 md:grid-cols-2';
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    class="flex h-full min-h-0 flex-col overflow-y-auto bg-lais-bg-page font-redHatText md:overflow-hidden"
  >
    <LaisHeader
      :title="
        isEditing ? 'Editar imóvel' : HOUSE_REGISTRATION_COPY.houseRegistration
      "
      :helper="HOUSE_REGISTRATION_COPY.reviewStepDescription"
      sticky
    >
      <template #actions>
        <LaisPrimaryButton variant="ghost" @click="cancelar">
          Cancelar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          variant="primary"
          icon="i-lucide-check"
          :is-loading="isSaving"
          @click="salvar"
        >
          {{
            isSaving
              ? HOUSE_REGISTRATION_COPY.loadingHouseRegistrationMessage
              : 'Salvar'
          }}
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <div
      ref="scrollContainer"
      class="min-h-0 flex-1 overflow-auto max-md:flex-none max-md:overflow-visible"
    >
      <div
        v-if="completion"
        class="mx-auto grid w-full max-w-6xl gap-5 px-4 py-8 sm:px-6 lg:px-10"
      >
        <LaisCard :title="HOUSE_REGISTRATION_COPY.loadingCompleted">
          <div class="grid gap-5">
            <div
              class="rounded-lais border border-lais-primary/20 bg-lais-primary-50 p-4"
            >
              <p class="text-[15px] font-semibold text-lais-text">
                Pronto, agora é só enviar o link de confirmação para o
                proprietário!
              </p>
              <p class="mt-1 text-[13px] leading-relaxed text-lais-text-body">
                Também vamos enviar uma mensagem automática para o proprietário.
              </p>
            </div>

            <div class="grid gap-3">
              <label
                class="text-[13px] font-semibold leading-tight text-lais-text-body"
              >
                Copie o link e envie para o proprietário
              </label>
              <div class="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
                <input :value="ownerConfirmationLink" readonly :class="input" />
                <LaisPrimaryButton
                  variant="ghost"
                  icon="i-lucide-copy"
                  @click="copiarLinkConfirmacao"
                >
                  Copiar link
                </LaisPrimaryButton>
              </div>
            </div>

            <div class="flex flex-wrap gap-2">
              <LaisPrimaryButton
                icon="i-lucide-arrow-right"
                @click="verImovelCriado"
              >
                {{ t('PROPERTIES.FORM.SUCCESS.OPEN_LISTING') }}
              </LaisPrimaryButton>
              <LaisPrimaryButton
                variant="ghost"
                icon="i-lucide-pencil-line"
                @click="irParaEditorCompleto"
              >
                Completar no editor
              </LaisPrimaryButton>
              <LaisPrimaryButton
                variant="ghost"
                icon="i-lucide-message-circle"
                @click="enviarLinkConfirmacaoWhatsapp"
              >
                Enviar link via WhatsApp
              </LaisPrimaryButton>
              <LaisPrimaryButton
                variant="ghost"
                icon="i-lucide-list-checks"
                @click="irParaRascunhos"
              >
                Ir para Rascunhos
              </LaisPrimaryButton>
              <LaisPrimaryButton
                variant="ghost"
                icon="i-lucide-plus"
                @click="cadastrarOutroImovel"
              >
                Cadastrar outro imóvel
              </LaisPrimaryButton>
            </div>
          </div>
        </LaisCard>

        <div
          class="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.8fr)]"
        >
          <LaisCard
            title="Acompanhe os próximos passos para publicação do imóvel"
          >
            <div class="grid gap-3">
              <div
                v-for="step in successSteps"
                :key="step.title"
                class="flex gap-3 rounded-lais border border-lais-border-soft bg-white p-4"
              >
                <span
                  class="mt-0.5 size-5 shrink-0 text-lais-primary"
                  :class="step.icon"
                  aria-hidden="true"
                />
                <div class="min-w-0">
                  <p class="text-[14px] font-semibold text-lais-text">
                    {{ step.title }}
                  </p>
                  <p
                    class="mt-1 text-[13px] leading-relaxed text-lais-text-body"
                  >
                    {{ step.description }}
                  </p>
                </div>
              </div>
            </div>
          </LaisCard>

          <LaisCard title="Assistente ativada">
            <p class="text-[13px] leading-relaxed text-lais-text-body">
              Acompanhe o andamento da conversa na área de Rascunhos.
            </p>
            <ul class="mt-4 grid gap-3">
              <li
                v-for="bullet in assistantSuccessBullets"
                :key="bullet"
                class="flex gap-2 text-[13px] leading-relaxed text-lais-text-body"
              >
                <span
                  class="i-lucide-check-circle mt-0.5 size-4 shrink-0 text-lais-primary"
                  aria-hidden="true"
                />
                <span>{{ bullet }}</span>
              </li>
            </ul>
          </LaisCard>
        </div>

        <div class="grid gap-5 lg:grid-cols-3">
          <LaisCard
            v-for="card in keyAccessCards"
            :key="card.title"
            :title="card.title"
          >
            <div class="flex h-full flex-col gap-4">
              <span
                class="size-6 text-lais-primary"
                :class="card.icon"
                aria-hidden="true"
              />
              <p class="text-[13px] leading-relaxed text-lais-text-body">
                {{ card.description }}
              </p>
              <LaisPrimaryButton
                class="mt-auto w-fit"
                variant="ghost"
                size="sm"
                @click="handleKeyAccessAction(card)"
              >
                {{ card.button }}
              </LaisPrimaryButton>
            </div>
          </LaisCard>
        </div>
      </div>

      <div
        v-else-if="!canManageImoveis"
        class="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6 lg:px-10"
      >
        <LaisPermissionState
          variant="denied"
          :title="t('PROPERTIES.FORM.PERMISSION.TITLE')"
          :description="t('PROPERTIES.FORM.PERMISSION.DESCRIPTION')"
        />
      </div>
      <div
        v-else-if="isLoading"
        class="mx-auto grid w-full max-w-6xl gap-5 px-4 py-8 sm:px-6 lg:px-10"
      >
        <LaisCardSkeleton :count="3" />
      </div>
      <div
        v-else-if="loadError"
        class="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6 lg:px-10"
      >
        <LaisErrorState
          :title="t('PROPERTIES.FORM.LOAD_ERROR.TITLE')"
          :description="t('PROPERTIES.FORM.LOAD_ERROR.DESCRIPTION')"
          :action-label="t('PROPERTIES.FORM.LOAD_ERROR.RETRY')"
          show-action
          @action="carregar"
        />
      </div>
      <form
        v-else
        class="w-full px-4 py-8 sm:px-6 lg:px-10"
        @submit.prevent="salvar"
      >
        <div class="mx-auto grid w-full max-w-6xl gap-5">
          <div
            class="rounded-lais border border-lais-border-soft bg-lais-bg-soft p-4 text-[13px] leading-relaxed text-lais-text-body"
          >
            {{ t('PROPERTIES.FORM.TABS.HELPER') }}
          </div>

          <div
            class="grid gap-4 rounded-lais border border-lais-primary/20 bg-white p-4 lg:grid-cols-[minmax(0,1fr)_auto]"
          >
            <div class="min-w-0">
              <p class="text-[14px] font-semibold text-lais-text">
                Owner canônico: {{ canonicalOwnerLabel }}
              </p>
              <p class="mt-1 text-[13px] leading-relaxed text-lais-text-body">
                Criar imóvel é o wizard simplificado de criação inicial. Este
                formulário é o editor completo e concentra estimativa, taxas,
                acessos e chaves usando o mesmo normalizador de payload.
              </p>
              <div class="mt-3 flex flex-wrap gap-2">
                <button
                  v-for="link in editorSectionLinks"
                  :key="link.id"
                  type="button"
                  class="inline-flex items-center gap-1.5 rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft px-3 py-2 text-[12px] font-semibold text-lais-text-body transition hover:border-lais-primary hover:text-lais-primary"
                  @click="abrirSecaoOperacional(link)"
                >
                  <span class="size-4" :class="link.icon" aria-hidden="true" />
                  {{ link.label }}
                </button>
              </div>
            </div>
            <div class="flex items-start">
              <LaisPrimaryButton
                v-if="!isEditing"
                type="button"
                variant="ghost"
                icon="i-lucide-route"
                @click="irParaCriarImovel"
              >
                Abrir wizard
              </LaisPrimaryButton>
            </div>
          </div>

          <LaisTabs
            v-model="activeFormTab"
            :tabs="formTabs"
            :aria-label="t('PROPERTIES.FORM.TABS.ARIA')"
          >
            <template #identidade>
              <div class="grid gap-5">
                <section class="md:col-span-2 grid gap-3">
                  <div>
                    <h2
                      class="text-[16px] font-semibold text-lais-text font-redHatDisplay"
                    >
                      {{ HOUSE_REGISTRATION_COPY.location }}
                    </h2>
                    <p class="mt-1 text-[13px] text-lais-text-muted">
                      Verifique se o imóvel está na área da Imudei ou se já está
                      cadastrado.
                    </p>
                  </div>
                  <LaisAddressField v-model="addressModel" />
                  <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <LaisField label="Complemento">
                      <input
                        v-model="addressMeta.complemento"
                        type="text"
                        :class="input"
                        placeholder="Apto, bloco, casa ou sala"
                      />
                    </LaisField>
                    <LaisField label="Andar">
                      <template #default>
                        <input
                          v-model="addressMeta.andar"
                          type="text"
                          :class="input"
                          placeholder="Ex: 8"
                        />
                        <span class="text-[12px] text-lais-text-muted">
                          Apenas para apartamentos ou kitnets
                        </span>
                      </template>
                    </LaisField>
                  </div>
                </section>

                <LaisCard class="md:col-span-2" title="Características">
                  <div
                    class="mb-5 rounded-lais border border-lais-border-soft bg-lais-bg-soft p-4"
                  >
                    <p class="text-[14px] font-semibold text-lais-text">
                      Descubra o valor correto para o imóvel
                    </p>
                    <p
                      class="mt-1 text-[13px] leading-relaxed text-lais-text-body"
                    >
                      Estas são as características mais relevantes para a
                      precificação de um imóvel. É importante realizar o
                      preenchimento correto para garantir a melhor sugestão de
                      preço.
                    </p>
                  </div>
                  <div :class="sectionGrid">
                    <LaisField label="Qual o tipo de imóvel?">
                      <select v-model="form.tipo" :class="input">
                        <option value="apartamento">Apartamento</option>
                        <option value="studio">Kitnet/Studio</option>
                        <option value="casa">Casa</option>
                        <option value="casa_condominio">
                          Casa em condomínio
                        </option>
                        <option value="cobertura">Cobertura</option>
                        <option value="sobrado">Sobrado</option>
                        <option value="loft">Loft</option>
                        <option value="flat">Flat</option>
                        <option value="terreno">Terreno</option>
                        <option value="comercial">Comercial</option>
                        <option value="sala_comercial">Sala comercial</option>
                        <option value="galpao">Galpão</option>
                        <option value="chacara">Chácara</option>
                      </select>
                    </LaisField>
                    <LaisField
                      label="Área do imóvel"
                      helper="Se não souber a área exata, inclua um valor aproximado"
                    >
                      <input
                        v-model.number="form.area_util"
                        type="number"
                        step="1"
                        min="1"
                        max="9999"
                        :class="input"
                        placeholder="Digite a área"
                      />
                    </LaisField>
                    <LaisField label="Área total (m²)">
                      <input
                        v-model.number="form.area_total"
                        type="number"
                        step="1"
                        min="0"
                        :class="input"
                      />
                    </LaisField>
                    <LaisField label="Qual o número de vagas de garagem?">
                      <input
                        v-model.number="form.vagas_garagem"
                        type="number"
                        min="0"
                        step="1"
                        :class="input"
                      />
                    </LaisField>
                    <LaisField
                      label="Qual o número de quartos?"
                      helper="Incluindo suítes"
                    >
                      <input
                        v-model.number="form.quartos"
                        type="number"
                        min="0"
                        step="1"
                        :class="input"
                      />
                    </LaisField>
                    <LaisField label="Destes, quantos são suites?">
                      <input
                        v-model.number="featureForm.suites"
                        type="number"
                        min="0"
                        step="1"
                        :class="input"
                      />
                    </LaisField>
                    <LaisField
                      label="Qual o número de banheiros?"
                      helper="Não incluir lavabo e serviço"
                    >
                      <input
                        v-model.number="form.banheiros"
                        type="number"
                        min="0"
                        step="1"
                        :class="input"
                      />
                    </LaisField>
                    <LaisField
                      label="Inquilinos podem ter animais de estimação?"
                      helper="Aceitar animais de estimação aumentam suas chances de alugar"
                    >
                      <select
                        v-model="featureForm.aceita_animais"
                        :class="input"
                      >
                        <option
                          v-for="option in YES_NO_UNKNOWN_OPTIONS"
                          :key="`pets-${option.value}`"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                    </LaisField>
                    <LaisField label="É uma cobertura?">
                      <select v-model="featureForm.cobertura" :class="input">
                        <option
                          v-for="option in YES_NO_OPTIONS"
                          :key="`cobertura-${option.value}`"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                    </LaisField>
                    <LaisField label="Possui ar condicionado?">
                      <select
                        v-model="featureForm.ar_condicionado"
                        :class="input"
                      >
                        <option
                          v-for="option in YES_NO_OPTIONS"
                          :key="`ac-${option.value}`"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                    </LaisField>
                    <LaisField label="Possui piscina?">
                      <select v-model="featureForm.piscina" :class="input">
                        <option
                          v-for="option in YES_NO_OPTIONS"
                          :key="`pool-${option.value}`"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                    </LaisField>
                    <LaisField label="Possui academia no condomínio?">
                      <select
                        v-model="featureForm.academia_condominio"
                        :class="input"
                      >
                        <option
                          v-for="option in YES_NO_OPTIONS"
                          :key="`gym-${option.value}`"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                    </LaisField>
                    <LaisField label="Perto do metrô?">
                      <select
                        v-model="featureForm.metro_proximo"
                        :class="input"
                      >
                        <option
                          v-for="option in YES_NO_UNKNOWN_OPTIONS"
                          :key="`subway-${option.value}`"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                    </LaisField>
                    <LaisField label="É mobiliado?">
                      <select v-model="featureForm.mobiliado" :class="input">
                        <option
                          v-for="option in YES_NO_OPTIONS"
                          :key="`furnished-${option.value}`"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                    </LaisField>
                    <div class="md:col-span-2">
                      <p
                        class="mb-2 text-[13px] font-semibold text-lais-text-body"
                      >
                        Alguns desses itens vai ficar no imóvel?
                      </p>
                      <div
                        class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3"
                      >
                        <label
                          v-for="item in FURNITURE_OPTIONS"
                          :key="item.value"
                          class="flex items-center gap-2 rounded-lais-sm border border-lais-border-soft bg-white px-3 py-2 text-[13px] text-lais-text cursor-pointer"
                        >
                          <input
                            v-model="featureForm.itens_mobiliados"
                            type="checkbox"
                            :value="item.value"
                            class="size-4 accent-lais-primary rounded"
                          />
                          {{ item.label }}
                        </label>
                      </div>
                    </div>
                  </div>
                </LaisCard>
              </div>
            </template>

            <template #anuncio>
              <div class="grid gap-5">
                <LaisCard
                  class="md:col-span-2"
                  :title="HOUSE_REGISTRATION_COPY.houseRegistration"
                >
                  <div :class="sectionGrid">
                    <LaisField class="md:col-span-2" label="Título" required>
                      <input
                        v-model="form.titulo"
                        type="text"
                        required
                        :class="input"
                        placeholder="Ex: Apartamento 3 quartos no Edifício Aurora"
                      />
                    </LaisField>
                    <LaisField
                      class="md:col-span-2"
                      label="Escreva uma descrição sobre o imóvel (opcional)"
                      helper="Compartilhe com os interessados as características únicas do imóvel."
                    >
                      <textarea
                        v-model="form.descricao"
                        rows="4"
                        :class="input"
                        placeholder="Descreva o imóvel"
                      />
                    </LaisField>
                    <LaisField label="Status">
                      <select v-model="form.status" :class="input">
                        <option value="disponivel">Disponível</option>
                        <option value="reservado">Reservado</option>
                        <option value="vendido">Vendido</option>
                        <option value="alugado">Alugado</option>
                        <option value="inativo">Inativo</option>
                      </select>
                    </LaisField>
                    <LaisField label="Finalidade">
                      <select v-model="form.finalidade" :class="input">
                        <option value="venda">Venda</option>
                        <option value="aluguel">Aluguel</option>
                        <option value="temporada">Temporada</option>
                      </select>
                    </LaisField>
                  </div>
                </LaisCard>

                <LaisCard
                  class="md:col-span-2"
                  :title="HOUSE_REGISTRATION_COPY.pricing"
                >
                  <div
                    class="mb-5 rounded-lais border border-lais-border-soft bg-lais-bg-soft p-4 text-[13px] leading-relaxed text-lais-text-body"
                  >
                    Para o anúncio ficar completo, vamos colher mais dados no
                    dia da sessão de fotos.
                  </div>
                  <div :class="sectionGrid">
                    <LaisField
                      v-if="isSaleContext"
                      label="Por quanto o imóvel será vendido?"
                    >
                      <input
                        v-model.number="form.valor_venda"
                        type="number"
                        step="1"
                        min="0"
                        :class="input"
                      />
                    </LaisField>
                    <LaisField
                      v-if="isRentContext"
                      label="Por quanto o imóvel será alugado?"
                    >
                      <input
                        v-model.number="form.valor_aluguel"
                        type="number"
                        step="1"
                        min="0"
                        :class="input"
                      />
                    </LaisField>
                    <LaisField
                      label="Valor do condomínio"
                      helper="Valor por mês do condomínio"
                    >
                      <input
                        v-model.number="form.valor_condominio"
                        type="number"
                        step="1"
                        min="0"
                        :class="input"
                      />
                    </LaisField>
                    <LaisField
                      label="O imóvel está sujeito ao pagamento de IPTU?"
                    >
                      <select v-model="featureForm.has_iptu" :class="input">
                        <option
                          v-for="option in IPTU_OPTIONS"
                          :key="`iptu-${option.value}`"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                    </LaisField>
                    <LaisField
                      v-if="shouldShowIptuValue"
                      label="Valor total anual do IPTU"
                      helper="Se tiver IPTUs adicionais (ex: garagem, depósito, etc), inclua junto"
                    >
                      <input
                        v-model.number="form.valor_iptu"
                        type="number"
                        step="1"
                        min="0"
                        :class="input"
                      />
                    </LaisField>
                    <div
                      id="imovel-tax-fee-governance"
                      class="md:col-span-2 grid gap-3 rounded-lais border border-lais-border-soft bg-white p-4"
                    >
                      <div
                        class="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between"
                      >
                        <div>
                          <p class="text-[14px] font-semibold text-lais-text">
                            Governança das taxas
                          </p>
                          <p
                            class="text-[13px] leading-relaxed text-lais-text-body"
                          >
                            Escolha a fonte canônica para IPTU e condomínio.
                            Valores importados divergentes ficam como fallback e
                            não sobrescrevem o canônico sem confirmação.
                          </p>
                        </div>
                      </div>

                      <div
                        v-for="row in taxFeeGovernanceRows"
                        :key="`tax-fee-${row.field}`"
                        class="grid gap-3 rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft p-3"
                      >
                        <div
                          class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between"
                        >
                          <div>
                            <p class="text-[13px] font-semibold text-lais-text">
                              {{ row.label }}
                            </p>
                            <p class="text-[12px] text-lais-text-body">
                              Valor canônico:
                              {{ formatCurrency(row.canonicalValue) }}
                            </p>
                          </div>
                          <div class="flex flex-wrap gap-2 text-[11px]">
                            <span
                              class="rounded-full bg-white px-2.5 py-1 font-semibold text-lais-text-body"
                            >
                              {{ row.sourceLabel }}
                            </span>
                            <span
                              class="rounded-full bg-white px-2.5 py-1 font-semibold text-lais-text-body"
                            >
                              {{ row.confidenceLabel }}
                            </span>
                            <span
                              class="rounded-full bg-white px-2.5 py-1 font-semibold text-lais-text-body"
                            >
                              {{ formatOriginDateLabel(row.originDate) }}
                            </span>
                          </div>
                        </div>

                        <div
                          v-if="row.conflict"
                          class="rounded-lais-sm border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] leading-relaxed text-amber-800"
                        >
                          Conflito/fallback:
                          {{ formatCurrency(row.fallbackValue) }}
                          recebido de
                          {{ row.fallbackSource || 'origem não informada' }}.
                          Salve com a fonte canônica escolhida para confirmar a
                          decisão.
                        </div>

                        <div class="grid gap-3 md:grid-cols-4">
                          <LaisField label="Fonte canônica">
                            <select
                              v-model="taxFeeForm[row.field].source_kind"
                              :class="input"
                            >
                              <option
                                v-for="option in TAX_FEE_SOURCE_OPTIONS"
                                :key="`${row.field}-source-${option.value}`"
                                :value="option.value"
                              >
                                {{ option.label }}
                              </option>
                            </select>
                          </LaisField>
                          <LaisField label="Estado">
                            <select
                              v-model="taxFeeForm[row.field].status"
                              :class="input"
                            >
                              <option
                                v-for="option in TAX_FEE_SOURCE_OPTIONS"
                                :key="`${row.field}-status-${option.value}`"
                                :value="option.value"
                              >
                                {{ option.label }}
                              </option>
                            </select>
                          </LaisField>
                          <LaisField label="Confiança">
                            <select
                              v-model="taxFeeForm[row.field].confidence"
                              :class="input"
                            >
                              <option
                                v-for="option in TAX_FEE_CONFIDENCE_OPTIONS"
                                :key="`${row.field}-confidence-${option.value}`"
                                :value="option.value"
                              >
                                {{ option.label }}
                              </option>
                            </select>
                          </LaisField>
                          <LaisField label="Data da origem">
                            <input
                              v-model="taxFeeForm[row.field].origin_date"
                              type="date"
                              :class="input"
                            />
                          </LaisField>
                        </div>

                        <div
                          v-if="row.history.length"
                          class="border-t border-lais-border-soft pt-2"
                        >
                          <p class="text-[12px] font-semibold text-lais-text">
                            Histórico da taxa
                          </p>
                          <ul
                            class="mt-2 grid gap-1 text-[12px] text-lais-text-body"
                          >
                            <li
                              v-for="entry in row.history.slice(-3).reverse()"
                              :key="entry.recorded_at || entry.origin_date"
                            >
                              {{
                                formatCurrency(
                                  entry.canonical_value || entry.value
                                )
                              }}
                              via {{ entry.source_label || entry.source_kind }}
                              <span v-if="entry.conflict">
                                com conflito/fallback
                              </span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <label
                      v-if="isSaleContext"
                      class="flex items-center gap-2 self-end rounded-lais-sm border border-lais-border-soft bg-white px-3 py-2.5 text-[13px] text-lais-text cursor-pointer"
                    >
                      <input
                        v-model="form.oferece_financiamento"
                        type="checkbox"
                        class="size-4 accent-lais-primary rounded"
                      />
                      Oferece simulação de financiamento
                    </label>
                    <div
                      id="imovel-price-estimate"
                      class="md:col-span-2 rounded-lais border border-lais-border-soft bg-white p-4"
                    >
                      <div
                        class="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div>
                          <p class="text-[13px] font-semibold text-lais-text">
                            {{ chosenPriceLabel }}
                          </p>
                          <p class="text-[20px] font-semibold text-lais-text">
                            {{ formatCurrency(currentBusinessPrice) }}
                          </p>
                        </div>
                        <span
                          v-if="pricingForm.prediction_certainty === 'alta'"
                          class="w-fit rounded-full bg-lais-primary-50 px-3 py-1 text-[12px] font-semibold text-lais-primary"
                        >
                          Alta confiança
                        </span>
                        <span
                          v-else-if="
                            pricingForm.prediction_certainty === 'limitada'
                          "
                          class="w-fit rounded-full bg-lais-bg-soft px-3 py-1 text-[12px] font-semibold text-lais-text-body"
                        >
                          Dados limitados na região
                        </span>
                      </div>
                      <LaisFieldFeedback
                        v-if="priceGuidance"
                        class="mt-3"
                        :label="priceGuidance.title"
                        :message="priceGuidance.message"
                        :state="
                          priceGuidance.kind === 'warning'
                            ? 'warning'
                            : 'success'
                        "
                      />
                    </div>
                    <div
                      class="md:col-span-2 rounded-lais border border-lais-border-soft bg-white p-4"
                    >
                      <div
                        class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
                      >
                        <div>
                          <p class="text-[14px] font-semibold text-lais-text">
                            Estimativa QPreço
                          </p>
                          <p
                            class="mt-1 text-[13px] leading-relaxed text-lais-text-body"
                          >
                            Solicite comparáveis da conta e aplique a sugestão
                            com motivo registrado no histórico do imóvel.
                          </p>
                        </div>
                        <LaisPrimaryButton
                          type="button"
                          size="sm"
                          icon="i-lucide-calculator"
                          :is-loading="priceEstimateLoading"
                          :disabled="priceEstimateLoading"
                          @click="solicitarEstimativaPreco"
                        >
                          Estimar preço
                        </LaisPrimaryButton>
                      </div>

                      <div
                        v-if="priceEstimateLoading"
                        class="mt-4 rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft px-3 py-3 text-[13px] text-lais-text-body"
                        aria-live="polite"
                      >
                        Calculando estimativa com imóveis comparáveis...
                      </div>
                      <div
                        v-else-if="priceEstimateError"
                        class="mt-4 rounded-lais-sm border border-red-200 bg-red-50 px-3 py-3 text-[13px] leading-relaxed text-red-700"
                      >
                        {{ priceEstimateError }}
                      </div>
                      <div
                        v-else-if="priceEstimate && !hasApplicablePriceEstimate"
                        class="mt-4 rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft px-3 py-3 text-[13px] leading-relaxed text-lais-text-body"
                      >
                        {{ priceEstimateEmptyMessage }}
                      </div>

                      <div
                        v-if="priceEstimate && hasApplicablePriceEstimate"
                        class="mt-4 grid gap-4"
                      >
                        <div
                          class="grid gap-3 rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft p-3 sm:grid-cols-2"
                        >
                          <p>
                            <span
                              class="block text-[11px] font-semibold uppercase text-lais-text-muted"
                            >
                              Valor estimado
                            </span>
                            <span
                              class="text-[18px] font-semibold text-lais-text"
                            >
                              {{ formatCurrency(estimatedPriceValue) }}
                            </span>
                          </p>
                          <p>
                            <span
                              class="block text-[11px] font-semibold uppercase text-lais-text-muted"
                            >
                              Faixa
                            </span>
                            <span class="text-[13px] text-lais-text">
                              {{ priceEstimateRangeLabel }}
                            </span>
                          </p>
                          <p>
                            <span
                              class="block text-[11px] font-semibold uppercase text-lais-text-muted"
                            >
                              Fonte
                            </span>
                            <span class="text-[13px] text-lais-text">
                              {{ priceEstimateSource }}
                            </span>
                          </p>
                          <p>
                            <span
                              class="block text-[11px] font-semibold uppercase text-lais-text-muted"
                            >
                              Confiança
                            </span>
                            <span class="text-[13px] text-lais-text">
                              {{ priceEstimateConfidenceLabel }}
                            </span>
                          </p>
                          <p>
                            <span
                              class="block text-[11px] font-semibold uppercase text-lais-text-muted"
                            >
                              Amostra
                            </span>
                            <span class="text-[13px] text-lais-text">
                              {{ priceEstimateSamples }} comparáveis
                            </span>
                          </p>
                          <p>
                            <span
                              class="block text-[11px] font-semibold uppercase text-lais-text-muted"
                            >
                              Data
                            </span>
                            <span class="text-[13px] text-lais-text">
                              {{ priceEstimateDateLabel }}
                            </span>
                          </p>
                        </div>

                        <LaisField
                          label="Motivo para aplicar"
                          helper="Obrigatório para registrar origem, diferença e auditoria da alteração."
                        >
                          <textarea
                            v-model="priceApplyReason"
                            rows="2"
                            :class="input"
                            placeholder="Ex: ajuste aprovado pelo proprietário após análise QPreço"
                          />
                        </LaisField>
                        <LaisPrimaryButton
                          type="button"
                          variant="primary"
                          size="sm"
                          icon="i-lucide-check-circle-2"
                          :disabled="!hasApplicablePriceEstimate"
                          @click="aplicarEstimativaPreco"
                        >
                          Aplicar estimativa
                        </LaisPrimaryButton>
                      </div>

                      <div
                        v-if="priceApplicationHistory.length"
                        class="mt-4 border-t border-lais-border-soft pt-3"
                      >
                        <p class="text-[12px] font-semibold text-lais-text">
                          Histórico de aplicações
                        </p>
                        <ul
                          class="mt-2 grid gap-2 text-[12px] text-lais-text-body"
                        >
                          <li
                            v-for="entry in priceApplicationHistory.slice(0, 3)"
                            :key="entry.confirmed_at || entry.confirmedAt"
                            class="rounded-lais-sm bg-lais-bg-soft px-3 py-2"
                          >
                            {{
                              formatCurrency(
                                entry.applied_price || entry.appliedPrice
                              )
                            }}
                            via
                            {{ entry.source || PRICE_ESTIMATE_SOURCE_FALLBACK }}
                            <span v-if="entry.reason">
                              - {{ entry.reason }}
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div class="md:col-span-2 grid gap-4 md:grid-cols-4">
                      <LaisField label="Outras sugestões" class="md:col-span-4">
                        <p class="text-[13px] text-lais-text-muted">
                          Acha que esse imóvel precisa de um ajuste no preço
                          sugerido? Edite o valor sugerido.
                        </p>
                      </LaisField>
                      <LaisField label="Mais rápido">
                        <input
                          v-model.number="pricingForm.faster_suggestion"
                          type="number"
                          step="1"
                          min="0"
                          :class="input"
                        />
                      </LaisField>
                      <LaisField label="Valor sugerido">
                        <input
                          v-model.number="pricingForm.recommended_price"
                          type="number"
                          step="1"
                          min="0"
                          :class="input"
                        />
                      </LaisField>
                      <LaisField label="Valor máximo">
                        <input
                          v-model.number="pricingForm.max_suggestion"
                          type="number"
                          step="1"
                          min="0"
                          :class="input"
                        />
                      </LaisField>
                      <LaisField label="Preço sugerido">
                        <select
                          v-model="pricingForm.prediction_certainty"
                          :class="input"
                        >
                          <option
                            v-for="option in PRICE_CERTAINTY_OPTIONS"
                            :key="`certainty-${option.value}`"
                            :value="option.value"
                          >
                            {{ option.label }}
                          </option>
                        </select>
                      </LaisField>
                    </div>
                    <div
                      class="md:col-span-2 rounded-lais border border-lais-border-soft bg-lais-bg-soft p-4"
                    >
                      <div
                        class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between"
                      >
                        <div class="min-w-0">
                          <p class="text-[14px] font-semibold text-lais-text">
                            {{ internalNegotiatedTitle }}
                          </p>
                          <p
                            class="mt-1 text-[13px] leading-relaxed text-lais-text-body"
                          >
                            Consideramos transações de imóveis próximos e
                            similares para estimar os preços de sua solicitação.
                            Nosso algoritmo prioriza o preço de imóveis que são
                            mais próximos e mais parecidos com o que você
                            solicitou.
                          </p>
                        </div>
                        <div
                          class="flex flex-wrap gap-2 text-[12px] font-semibold"
                        >
                          <span
                            class="rounded-full bg-white px-3 py-1 text-lais-text-body"
                          >
                            {{ internalNegotiatedStatus }}
                          </span>
                          <span
                            class="rounded-full bg-white px-3 py-1 text-lais-text-body"
                          >
                            Próximo
                          </span>
                          <span
                            class="rounded-full bg-white px-3 py-1 text-lais-text-body"
                          >
                            Mesmo condomínio
                          </span>
                          <span
                            class="rounded-full bg-white px-3 py-1 text-lais-text-body"
                          >
                            /m²
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </LaisCard>

                <LaisCard
                  class="md:col-span-2"
                  title="Envie esta avaliação do imóvel para o proprietário"
                >
                  <div class="grid gap-4">
                    <p class="text-[13px] leading-relaxed text-lais-text-body">
                      Compartilhe os resultados e garanta o melhor preço para o
                      anúncio deste imóvel
                    </p>
                    <div :class="sectionGrid">
                      <LaisField
                        class="md:col-span-2"
                        label="O link abaixo pode ser copiado e enviado"
                      >
                        <input
                          v-model="pricingForm.report_link"
                          type="url"
                          :class="input"
                          placeholder="https://imudei.app/relatorio/..."
                        />
                      </LaisField>
                      <LaisField label="Corretor">
                        <input
                          v-model="pricingForm.report_agent_name"
                          type="text"
                          :class="input"
                        />
                      </LaisField>
                      <LaisField label="Empresa">
                        <input
                          v-model="pricingForm.report_company"
                          type="text"
                          :class="input"
                        />
                      </LaisField>
                    </div>
                    <button
                      type="button"
                      class="inline-flex w-fit items-center justify-center rounded-lais-sm bg-lais-primary px-4 py-2 text-[13px] font-semibold text-white transition hover:bg-lais-primary-700"
                      @click="compartilharRelatorio"
                    >
                      Compartilhar no WhatsApp
                    </button>
                  </div>
                </LaisCard>

                <LaisCard
                  class="md:col-span-2"
                  :title="HOUSE_REGISTRATION_COPY.ownerInformation"
                >
                  <div :class="sectionGrid">
                    <LaisField label="Proprietário">
                      <input
                        v-model="ownerForm.nome"
                        type="text"
                        :class="input"
                      />
                    </LaisField>
                    <LaisField label="Telefone">
                      <input
                        v-model="ownerForm.telefone"
                        type="tel"
                        :class="input"
                      />
                    </LaisField>
                    <LaisField label="E-mail">
                      <input
                        v-model="ownerForm.email"
                        type="email"
                        :class="input"
                      />
                    </LaisField>
                    <LaisField label="Responsável pelo imóvel">
                      <select v-model="ownerForm.perfil" :class="input">
                        <option
                          v-for="option in OWNER_PROFILE_OPTIONS"
                          :key="`owner-${option.value}`"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                    </LaisField>
                    <LaisField
                      label="Código de referência"
                      helper="Código usado no portal de origem"
                    >
                      <input
                        v-model="form.external_id"
                        type="text"
                        :class="input"
                      />
                    </LaisField>
                    <LaisField label="Portal de origem">
                      <select v-model="form.portal_origem" :class="input">
                        <option
                          v-for="portal in PORTAIS"
                          :key="portal.value || 'nao-informado'"
                          :value="portal.value"
                        >
                          {{ portal.label }}
                        </option>
                      </select>
                    </LaisField>
                    <LaisField
                      class="md:col-span-2"
                      label="Link do imóvel, empreendimento ou outro"
                      helper="Opcional"
                    >
                      <input
                        v-model="form.link_externo"
                        type="url"
                        :class="input"
                        placeholder="https://…"
                      />
                    </LaisField>
                  </div>
                </LaisCard>

                <LaisCard
                  class="md:col-span-2"
                  :title="HOUSE_REGISTRATION_COPY.rentExclusivity"
                >
                  <div :class="sectionGrid">
                    <LaisField label="Exclusividade">
                      <select
                        v-model="operationForm.rent_exclusivity"
                        :class="input"
                      >
                        <option
                          v-for="option in EXCLUSIVITY_OPTIONS"
                          :key="`exclusivity-${option.value}`"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                    </LaisField>
                  </div>
                </LaisCard>

                <LaisCard class="md:col-span-2" title="Revisar anúncio">
                  <div class="grid gap-5">
                    <p class="text-[13px] leading-relaxed text-lais-text-body">
                      Caso note algo diferente, corrija abaixo as informações do
                      imóvel
                    </p>
                    <div
                      class="rounded-lais border border-lais-primary-100 bg-lais-primary-50 p-4"
                    >
                      <div
                        class="flex flex-col gap-3 sm:flex-row sm:items-start"
                      >
                        <span
                          class="grid size-9 shrink-0 place-items-center rounded-full bg-white text-lais-primary"
                        >
                          <i class="i-lucide-sparkles size-4" />
                        </span>
                        <div class="min-w-0">
                          <p class="text-[14px] font-semibold text-lais-text">
                            Preenchemos automaticamente
                          </p>
                          <p
                            class="mt-1 text-[13px] leading-relaxed text-lais-text-body"
                          >
                            Confira os itens preenchidos pela nossa
                            inteligência, vamos coletar mais dados na sessão de
                            fotos.
                          </p>
                        </div>
                      </div>
                    </div>
                    <LaisField label="Portaria">
                      <select
                        v-model="listingReviewForm.doorman"
                        :class="input"
                      >
                        <option
                          v-for="option in DOORMAN_OPTIONS"
                          :key="`doorman-${option.value}`"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                    </LaisField>
                    <div>
                      <p
                        class="mb-2 text-[13px] font-semibold text-lais-text-body"
                      >
                        Condomínio
                      </p>
                      <div
                        class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4"
                      >
                        <label
                          v-for="item in CONDOMINIUM_AMENITIES"
                          :key="item.value"
                          class="flex items-center gap-2 rounded-lais-sm border border-lais-border-soft bg-white px-3 py-2 text-[13px] text-lais-text cursor-pointer"
                        >
                          <input
                            v-model="listingReviewForm.condominium_amenities"
                            type="checkbox"
                            :value="item.value"
                            class="size-4 accent-lais-primary rounded"
                          />
                          {{ item.label }}
                        </label>
                      </div>
                    </div>
                    <div>
                      <p
                        class="mb-2 text-[13px] font-semibold text-lais-text-body"
                      >
                        Comodidades
                      </p>
                      <div
                        class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4"
                      >
                        <label
                          v-for="item in PROPERTY_AMENITIES"
                          :key="item.value"
                          class="flex items-center gap-2 rounded-lais-sm border border-lais-border-soft bg-white px-3 py-2 text-[13px] text-lais-text cursor-pointer"
                        >
                          <input
                            v-model="listingReviewForm.property_amenities"
                            type="checkbox"
                            :value="item.value"
                            class="size-4 accent-lais-primary rounded"
                          />
                          {{ item.label }}
                        </label>
                      </div>
                    </div>
                    <LaisField label="Enviar correções">
                      <textarea
                        v-model="listingReviewForm.notes"
                        rows="4"
                        :class="input"
                        placeholder="Descreva o que precisa ser ajustado no anúncio"
                      />
                    </LaisField>
                  </div>
                </LaisCard>

                <LaisCard class="md:col-span-2" title="Visitas">
                  <div :class="sectionGrid">
                    <LaisField v-if="isRentContext" label="Visitas de aluguel">
                      <select
                        v-model="operationForm.rent_visit_priority"
                        :class="input"
                      >
                        <option
                          v-for="option in VISIT_PRIORITY_OPTIONS"
                          :key="`rent-visit-${option.value}`"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                    </LaisField>
                    <LaisField v-if="isSaleContext" label="Visitas de venda">
                      <select
                        v-model="operationForm.sale_visit_priority"
                        :class="input"
                      >
                        <option
                          v-for="option in VISIT_PRIORITY_OPTIONS"
                          :key="`sale-visit-${option.value}`"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                    </LaisField>
                    <LaisField
                      id="imovel-key-access"
                      label="Tipo de acesso"
                      helper="Como o parceiro irá acessar o imóvel."
                    >
                      <select
                        v-model="operationForm.entry_access_type"
                        :class="input"
                      >
                        <option
                          v-for="option in ENTRY_ACCESS_OPTIONS"
                          :key="`entry-access-${option.value}`"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                    </LaisField>
                    <LaisField class="md:col-span-2" label="Detalhes da visita">
                      <textarea
                        v-model="operationForm.visit_notes"
                        rows="3"
                        :class="input"
                      />
                    </LaisField>
                  </div>
                </LaisCard>
              </div>
            </template>

            <template #midia>
              <div class="grid gap-5">
                <LaisCard
                  class="md:col-span-2"
                  :title="HOUSE_REGISTRATION_COPY.photoScheduling"
                >
                  <div class="grid gap-4">
                    <p class="text-[13px] leading-relaxed text-lais-text-body">
                      Para o anúncio ficar completo, vamos colher mais dados no
                      dia da sessão de fotos.
                    </p>
                    <div
                      class="grid gap-2 rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft p-3 sm:grid-cols-2"
                    >
                      <span
                        v-for="requirement in MEDIA_PUBLICABILITY_FORM_REQUIREMENTS"
                        :key="requirement"
                        class="inline-flex items-center gap-2 text-[12px] font-semibold text-lais-text-body"
                      >
                        <span
                          class="i-lucide-shield-check size-4 text-lais-primary"
                          aria-hidden="true"
                        />
                        {{ requirement }}
                      </span>
                    </div>
                    <div :class="sectionGrid">
                      <LaisField
                        :label="HOUSE_REGISTRATION_COPY.photoScheduling"
                      >
                        <select
                          v-model="operationForm.photo_session_status"
                          :class="input"
                        >
                          <option
                            v-for="option in PHOTO_SESSION_OPTIONS"
                            :key="`photo-${option.value}`"
                            :value="option.value"
                          >
                            {{ option.label }}
                          </option>
                        </select>
                      </LaisField>
                      <LaisField label="Data da sessão de fotos">
                        <input
                          v-model="operationForm.photo_session_date"
                          type="datetime-local"
                          :class="input"
                        />
                      </LaisField>
                    </div>
                    <LaisField
                      label="Mídias"
                      helper="Informe uma URL por linha. A primeira será sincronizada como capa pendente de QA."
                    >
                      <textarea
                        v-model="formText.midias"
                        rows="5"
                        class="font-mono text-[13px]"
                        :class="[input]"
                        placeholder="https://cdn.exemplo.com/imovel-1.jpg&#10;https://cdn.exemplo.com/imovel-2.jpg"
                      />
                    </LaisField>
                    <LaisField
                      label="Características adicionais"
                      helper="Use o formato chave: valor, uma por linha."
                    >
                      <textarea
                        v-model="formText.caracteristicas"
                        rows="6"
                        class="font-mono text-[13px]"
                        :class="[input]"
                        placeholder="suite: 1&#10;andar: 8&#10;aceita_pet: sim"
                      />
                    </LaisField>
                  </div>
                </LaisCard>
              </div>
            </template>
          </LaisTabs>
        </div>
      </form>
    </div>

    <footer
      class="flex flex-col-reverse gap-2 border-t border-lais-border-rule bg-white px-4 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-[0_-12px_30px_rgba(15,23,42,0.08)] lg:hidden"
    >
      <LaisPrimaryButton variant="ghost" @click="cancelar">
        Cancelar
      </LaisPrimaryButton>
      <LaisPrimaryButton
        variant="primary"
        icon="i-lucide-check"
        :is-loading="isSaving"
        @click="salvar"
      >
        {{
          isSaving
            ? HOUSE_REGISTRATION_COPY.loadingHouseRegistrationMessage
            : 'Salvar'
        }}
      </LaisPrimaryButton>
    </footer>

    <LaisConfirmModal
      :show="isApplyEstimateModalOpen"
      title="Aplicar estimativa de preço?"
      :description="applyEstimateConfirmationDescription"
      confirm-text="Aplicar estimativa"
      confirm-variant="primary"
      @close="fecharModalAplicarEstimativa"
      @confirm="confirmarAplicacaoEstimativa"
    />
  </section>
</template>
